import { BULLET_DESPAWN_DISTANCE_FROM_PLAYER } from "./constants"
import { Container, Assets, AnimatedSprite, Texture } from "pixi.js"
import { applyVelocity } from "./lib/apply-velocity"
import { applyFriction } from "./lib/apply-friction"

export async function Projectiles({ isSolid }) {
  const projectiles = new Container()

  await Assets.load("res/bullet.json")

  const make = (name, nFrames) => {
    return [...Array(nFrames)].map((_, i) => Texture.from(`${name} ${i}`))
  }

  const frames = {
    bullet: make("bullet", 1),
  }

  let nextId = 0

  const spawnProjectile = (x, y, type, angle, speed, damage, baseVelocity = null) => {
    const projectile = new AnimatedSprite(frames[type])
    projectile.id = ++nextId
    projectile.anchor.set(0.5, 0.5)
    projectile.x = x
    projectile.y = y
    projectile.damage = damage
    projectile.velocity = {
      x: Math.cos(angle) * speed + (baseVelocity?.x ?? 0),
      y: Math.sin(angle) * speed + (baseVelocity?.y ?? 0),
    }
    projectile.animationSpeed = 0.1
    projectile.isActive = true
    projectile.startVelocity = {
      x: projectile.velocity.x,
      y: projectile.velocity.y,
    }

    // TODO: when implementing animated projectiles, call play() on projectile

    projectiles.addChild(projectile)
  }

  const tick = (time, { player, spawnParticle }) => {
    let childrenToRemove = []
    // ^- remove after-the-fact, assuming removal during forEach() is unsafe
    projectiles.children.forEach((projectile) => {
      if (!projectile.isActive) return

      const line = applyVelocity(projectile, { time, isSolid })
      addTrail({ line, spawnParticle })

      applyFriction(projectile, { time, friction: 0.02 })

      if (projectile.velocity.x == 0 && projectile.velocity.y == 0) {
        childrenToRemove.push(projectile)
        return
      }

      projectile.velocity.y +=
        (Math.abs(projectile.startVelocity.x) -
          Math.abs(projectile.velocity.x)) *
        0.003

      // Check distance from player, remove if too far
      const distance = Math.sqrt(
        (projectile.x - player.x) ** 2 + (projectile.y - player.y) ** 2,
      )
      if (distance > BULLET_DESPAWN_DISTANCE_FROM_PLAYER)
        childrenToRemove.push(projectile)
    })
    projectiles.removeChild(...childrenToRemove)
  }

  return { projectiles, spawnProjectile, tick }
}

function addTrail({ line, spawnParticle }) {
  let { x0, y0, x1, y1 } = line
  x0 = Math.round(x0)
  y0 = Math.round(y0)
  x1 = Math.round(x1)
  y1 = Math.round(y1)

  if (x0 === x1 && y0 === y1) return

  // Bresenham's line algorithm
  const dx = Math.abs(x1 - x0)
  const dy = Math.abs(y1 - y0)
  const sx = Math.sign(x1 - x0)
  const sy = Math.sign(y1 - y0)
  let err = dx - dy

  while (true) {
    spawnParticle({ x: x0, y: y0, duration: 100, color: "#fff1" })

    if (x0 === x1 && y0 === y1) break

    const e2 = 2 * err
    if (e2 > -dy) {
      err -= dy
      x0 += sx
    }
    if (e2 < dx) {
      err += dx
      y0 += sy
    }
  }
}
